import { defineStore } from 'pinia';
import type { DangerSignFinding } from '../types';

export interface DangerSignState {
  findings: Record<string, DangerSignFinding>;
  acknowledged: string[];
  pendingAlert: DangerSignFinding[];
  checkVersion: number;
  stopRequested: boolean;
}

function createInitialDangerSignState(): DangerSignState {
  return {
    findings: {},
    acknowledged: [],
    pendingAlert: [],
    checkVersion: 0,
    stopRequested: false,
  };
}

// Not an action, so a stop never reaches the check's listener as an acknowledge.
function acknowledgePending(state: DangerSignState): void {
  const names = state.pendingAlert.map(finding => finding.name);
  state.acknowledged = [...state.acknowledged, ...names.filter(name => !state.acknowledged.includes(name))];
  state.pendingAlert = [];
}

export const useDangerSignStore = defineStore('dangerSign', {
  state: (): DangerSignState => createInitialDangerSignState(),

  getters: {
    hasPendingAlert: (state): boolean => state.pendingAlert.length > 0,
    hasFindings: (state): boolean => Object.keys(state.findings).length > 0,
  },

  actions: {
    beginCheck(): number {
      this.checkVersion += 1;
      this.pendingAlert = [];
      return this.checkVersion;
    },

    applyCheck(version: number, positives: DangerSignFinding[]): boolean {
      if (version !== this.checkVersion) return false;

      this.findings = Object.fromEntries(positives.map(finding => [finding.name, finding]));
      this.acknowledged = this.acknowledged.filter(name => name in this.findings);
      this.pendingAlert = positives.filter(finding => !this.acknowledged.includes(finding.name));
      // Going Back and correcting the answer that triggered the stop means nothing is positive
      // any more, so the encounter carries on as an ordinary one.
      if (positives.length === 0) this.stopRequested = false;
      return true;
    },

    supersede(): void {
      this.checkVersion += 1;
      this.pendingAlert = [];
    },

    // The alert was seen and dismissed, so the worker is carrying on rather than stopping.
    acknowledge(): void {
      acknowledgePending(this);
      this.stopRequested = false;
    },

    // A check still scoring must not alert on the triage screen, and Back into the questions must not re-alert.
    requestStop(): void {
      acknowledgePending(this);
      this.checkVersion += 1;
      this.stopRequested = true;
    },

    // The version only ever rises, so a check still scoring for the previous encounter can never apply here.
    reset(): void {
      Object.assign(this, { ...createInitialDangerSignState(), checkVersion: this.checkVersion + 1 });
    },

    resume(snapshot?: Partial<DangerSignState>): void {
      Object.assign(this, {
        ...createInitialDangerSignState(),
        ...snapshot,
        pendingAlert: [],
        checkVersion: this.checkVersion + 1,
      });
    },
  },
});
